import { apiFetch, attendreTache, EN_TETE_TACHE_ASYNCHRONE } from './api';

/**
 * Génère (ou régénère) le contrat de formation d'un devis.
 * Le contrat reprend le devis tel quel : formations, durées et montants.
 */
export function genererContrat(devisId, body = {}) {
  return apiFetch(`/devis/${devisId}/contrat`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Envoie le texte, les fichiers et l'entité à la création depuis un texte :
 * multipart, tâche de fond attendue (la lecture IA est longue).
 */
async function posterDepuisTexte(chemin, texte, entite, fichiers, { formationId = null, proposition = null } = {}) {
  // Multipart : le texte collé et les fichiers joints (contrat, convocation,
  // feuille reçus) voyagent ensemble ; l'un ou l'autre peut manquer.
  const corps = new FormData();
  corps.append('texte', texte || '');
  corps.append('entite', entite);
  // Pour un texte qui ne nomme pas la formation : celle choisie dans le formulaire.
  if (formationId) corps.append('formationId', String(formationId));
  // Le dossier relu et corrigé à l'écran : c'est lui qui est créé.
  if (proposition) corps.append('proposition', JSON.stringify(proposition));
  for (const fichier of fichiers || []) corps.append('fichiers', fichier);
  const res = await fetch(`${process.env.REACT_APP_API_URL || '/api'}${chemin}`, {
    method: 'POST',
    credentials: 'include',
    headers: EN_TETE_TACHE_ASYNCHRONE,
    body: corps,
  });
  const json = await res.json().catch(() => ({}));
  // Lancé en tâche de fond : on attend son résultat (même forme).
  if (res.status === 202 && json.tacheId) return attendreTache(json.tacheId);
  if (!res.ok) {
    const err = new Error(json?.message || `Création impossible (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return json;
}

/**
 * Crée un contrat, une convocation ou une feuille de présence depuis un
 * texte collé : l'IA lit le texte, le dossier est posé d'un trait. Rien
 * n'est envoyé au client. `entite` : 'contrat' | 'convocation' | 'feuille_presence'.
 * Avec `proposition` (celle de `lireDepuisTexte`, relue et corrigée), c'est
 * elle qui est créée, sans nouvelle lecture.
 */
export function creerDepuisTexte(texte, entite = 'contrat', fichiers = [], options = {}) {
  return posterDepuisTexte('/contrats/depuis-texte', texte, entite, fichiers, options);
}

/**
 * Premier temps : l'IA lit et PROPOSE le dossier (client, séances, participants),
 * sans rien créer. À relire et corriger avant `creerDepuisTexte(…, { proposition })`.
 */
export function lireDepuisTexte(texte, entite = 'contrat', fichiers = [], options = {}) {
  return posterDepuisTexte('/contrats/depuis-texte/lecture', texte, entite, fichiers, options);
}

export function getContratParDevis(devisId) {
  return apiFetch(`/devis/${devisId}/contrat`);
}

/** Dernier contrat d'une demande, tous devis confondus. */
export function getContratParContact(contactId) {
  return apiFetch(`/contacts/${contactId}/contrat`);
}

/**
 * Signature électronique du responsable de l'organisme de formation
 * (tracé sur le canvas de signature), depuis le panneau interne.
 */
export function signerContratOrganisme(id, imageDataUrl) {
  return apiFetch(`/contrats/${id}/signature-organisme`, {
    method: 'POST',
    body: JSON.stringify({ image: imageDataUrl }),
  });
}

/** Contrat exposé via le jeton reçu par email (aucune authentification). */
export function getContratParToken(token) {
  return apiFetch(`/contrats/reponse/${token}`);
}

/** Signature électronique du client (tracé sur le canvas), depuis le lien reçu par email. */
export function signerContrat(token, imageDataUrl) {
  return apiFetch(`/contrats/reponse/${token}/signature`, {
    method: 'POST',
    body: JSON.stringify({ image: imageDataUrl }),
  });
}

/**
 * URL du PDF. Le serveur le produit à la volée s'il n'existe pas encore.
 * Avec `token` (page publique de signature), la route par jeton est utilisée :
 * le client n'a pas de session, le jeton reçu par email fait foi.
 */
export function urlPdfContrat(id, { forcer = false, apercu = false, token = null } = {}) {
  const base = process.env.REACT_APP_API_URL || '/api';
  const params = [];
  if (forcer) params.push('forcer=1');
  if (apercu) params.push('apercu=1');
  const chemin = token ? `/contrats/reponse/${token}/pdf` : `/contrats/${id}/pdf`;
  return `${base}${chemin}${params.length ? `?${params.join('&')}` : ''}`;
}

/** Nom de fichier annoncé par le serveur dans Content-Disposition. */
function nomDepuisEntete(entete, defaut) {
  const trouve = /filename="?([^"]+)"?/.exec(entete || '');
  return trouve ? trouve[1] : defaut;
}

/**
 * Télécharge le PDF du contrat et déclenche l'enregistrement côté navigateur.
 * Même mécanique que pour le devis : fetch plutôt qu'un lien, pour connaître
 * la fin du rendu et remonter une erreur lisible.
 */
export async function telechargerPdfContrat(id, { forcer = false, token = null } = {}) {
  // credentials : le cookie de session part avec l'appel (cf. api.js).
  const res = await fetch(urlPdfContrat(id, { forcer, token }), { credentials: 'include' });

  if (!res.ok) {
    let message = `Génération du contrat impossible (${res.status})`;
    try {
      const corps = await res.json();
      if (corps?.message) message = corps.message;
    } catch {
      /* réponse non JSON : on garde le code HTTP */
    }
    const err = new Error(message);
    err.status = res.status;
    throw err;
  }

  const nom = nomDepuisEntete(res.headers.get('Content-Disposition'), `contrat-${id}.pdf`);
  const blob = await res.blob();

  const url = URL.createObjectURL(blob);
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nom;
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  URL.revokeObjectURL(url);

  return nom;
}

/** Les paramètres vides ne sont pas transmis : le serveur applique ses défauts. */
function versQuery(params) {
  const query = new URLSearchParams();
  for (const [cle, valeur] of Object.entries(params)) {
    if (valeur !== undefined && valeur !== null && valeur !== '') query.set(cle, valeur);
  }
  const s = query.toString();
  return s ? `?${s}` : '';
}

/** Tous les contrats générés, paginés (page Contrat du back-office). */
export function getContrats(params = {}) {
  return apiFetch(`/contrats${versQuery(params)}`);
}

/**
 * Contrats qu'on peut présenter à la signature d'un client présent (page
 * Clients > Signature contrat) : ceux que l'organisme de formation a signés.
 */
export function getContratsASigner(params = {}) {
  return apiFetch(`/contrats/a-signer${versQuery(params)}`);
}

/**
 * Ouvre la signature d'un contrat pour un client présent : le serveur rend
 * le jeton du lien de signature, avec lequel on atteint la même page que
 * celle du lien reçu par email, sans email.
 */
export function ouvrirSignatureContrat(id) {
  return apiFetch(`/contrats/${id}/lien-signature`, { method: 'POST' });
}

/**
 * Contrat complet. La liste ne renvoie qu'une projection (assez pour le
 * tableau, pas pour le formulaire de correction) : le panneau doit relire
 * le contrat entier, sinon il enregistrerait des champs vides à la place
 * de ceux qu'il n'a jamais reçus.
 */
export function getContratById(id) {
  return apiFetch(`/contrats/${id}`);
}

/**
 * URL de l'aperçu HTML : le gabarit dont le PDF est tiré. Affiché dans une
 * iframe par le panneau du contrat, pour que l'écran montre exactement le
 * document que le client recevra.
 */
export function urlApercuContrat(id) {
  const base = process.env.REACT_APP_API_URL || '/api';
  return `${base}/contrats/${id}/apercu`;
}

/**
 * Corrige les mentions propres au contrat (dates, lieu, interlocuteur).
 * Les montants et les prestations viennent du devis et ne sont pas
 * modifiables ici.
 */
/** Supprime un contrat, avec ses journées et documents. Le devis reste. */
export function deleteContrat(id) {
  return apiFetch(`/contrats/${id}`, { method: 'DELETE' });
}

export function modifierContrat(id, champs) {
  return apiFetch(`/contrats/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(champs),
  });
}

/** Envoie (ou renvoie) le contrat au client, avec son lien de signature. */
export function envoyerContrat(id, body = {}) {
  return apiFetch(`/contrats/${id}/envoyer`, { method: 'POST', body: JSON.stringify(body) });
}

/** L'email du contrat tel qu'il partirait : objet, corps, destinataire. */
export function apercuEnvoiContrat(id) {
  return apiFetch(`/contrats/${id}/envoyer/apercu`);
}

/**
 * Applique un modèle au contrat : ses articles sont recopiés et deviennent
 * le texte de CE contrat, que l'éditeur peut ensuite retoucher.
 */
export function appliquerModeleContrat(id, modeleId) {
  return apiFetch(`/contrats/${id}/modele`, {
    method: 'POST',
    body: JSON.stringify({ modeleId }),
  });
}

/**
 * Enregistre le texte retouché dans l'éditeur : en-tête (titre, sous-titre,
 * référence légale, préambule) et articles, en bloc.
 */
export function modifierTexteContrat(id, texte) {
  return apiFetch(`/contrats/${id}/texte`, {
    method: 'PUT',
    body: JSON.stringify(texte),
  });
}

/** Lit un message d'erreur JSON du serveur, sinon un libellé avec le code HTTP. */
async function erreurDe(res, defaut) {
  let message = `${defaut} (${res.status})`;
  try {
    const corps = await res.json();
    if (corps?.message) message = corps.message;
  } catch {
    /* réponse non JSON */
  }
  const err = new Error(message);
  err.status = res.status;
  return err;
}

/**
 * Télécharge le contrat en Word (.docx) : le Word importé s'il existe,
 * sinon le document bâti du gabarit. Même mécanique que le PDF : fetch
 * plutôt qu'un lien, pour remonter une erreur lisible.
 */
export async function telechargerDocxContrat(id) {
  const base = process.env.REACT_APP_API_URL || '/api';
  const res = await fetch(`${base}/contrats/${id}/docx`, { credentials: 'include' });
  if (!res.ok) throw await erreurDe(res, 'Export Word impossible');

  const nom = nomDepuisEntete(res.headers.get('Content-Disposition'), `contrat-${id}.docx`);
  const url = URL.createObjectURL(await res.blob());
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nom;
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  URL.revokeObjectURL(url);
  return nom;
}

/** Ré-importe le Word retouché : il devient le corps du contrat. */
export async function importerDocxContrat(id, fichier) {
  const base = process.env.REACT_APP_API_URL || '/api';
  const donnees = new FormData();
  donnees.append('fichier', fichier, fichier.name);
  // Pas de Content-Type : le navigateur pose le multipart et sa frontière.
  const res = await fetch(`${base}/contrats/${id}/docx`, {
    method: 'POST',
    body: donnees,
    credentials: 'include',
  });
  if (!res.ok) throw await erreurDe(res, 'Import du Word impossible');
  return res.json();
}

/** Oublie le Word importé : le contrat reprend le texte du gabarit ou du modèle. */
export function retirerDocxContrat(id) {
  return apiFetch(`/contrats/${id}/docx`, { method: 'DELETE' });
}

/**
 * Génère un type de document pour toutes les journées du contrat : convocation,
 * feuille_presence, attestation, convocation_formateur, contrat_formateur.
 */
export function genererDocumentsContrat(contratId, type) {
  return apiFetch(`/contrats/${contratId}/documents/${encodeURIComponent(type)}`, { method: 'POST' });
}

/** Refait le PDF du contrat (bouton « Régénérer le PDF » de la fiche). */
export function regenererPdfContrat(id) {
  return apiFetch(`/contrats/${id}/pdf/regenerer`, { method: 'POST' });
}

/* ------------------------------------------------------------------ */
/* Parapheur : les fichiers de contrats déposés, à transformer en contrats */
/* ------------------------------------------------------------------ */

export function getParapheur(params = {}) {
  return apiFetch(`/contrats/parapheur${versQuery(params)}`);
}

/** Dépose plusieurs fichiers d'un coup (champ multipart `fichiers`). */
export async function deposerParapheur(fichiers, { contactId = null } = {}) {
  const base = process.env.REACT_APP_API_URL || '/api';
  const donnees = new FormData();
  for (const f of fichiers) donnees.append('fichiers', f, f.name);
  if (contactId) donnees.append('contactId', String(contactId));
  const res = await fetch(`${base}/contrats/parapheur`, {
    method: 'POST',
    body: donnees,
    credentials: 'include',
  });
  const corps = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(corps?.message || `Dépôt impossible (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return corps;
}

export function urlFichierParapheur(id, { apercu = false } = {}) {
  const base = process.env.REACT_APP_API_URL || '/api';
  return `${base}/contrats/parapheur/${id}/fichier${apercu ? '?apercu=1' : ''}`;
}

export function modifierFichierParapheur(id, champs) {
  return apiFetch(`/contrats/parapheur/${id}`, { method: 'PATCH', body: JSON.stringify(champs) });
}

export function supprimerFichierParapheur(id) {
  return apiFetch(`/contrats/parapheur/${id}`, { method: 'DELETE' });
}

/** Crée l'entité contrat (origine « externe ») à partir du fichier. */
export function transformerFichierParapheur(id, { contactId, intitule } = {}) {
  return apiFetch(`/contrats/parapheur/${id}/transformer`, {
    method: 'POST',
    body: JSON.stringify({ contactId, intitule }),
  });
}
